import { eq, or, type SQL } from "drizzle-orm";
import type { AnyMySqlColumn } from "drizzle-orm/mysql-core";
import { SCOPE, type ScopeId } from "./scopes";

/**
 * Turn a permission scope into a query condition to AND into a company-scoped list/detail query
 * (reskin-docs/03 §6). Returns:
 *   - `null`  → ALL: add nothing (company scope only)
 *   - `SQL`   → the ownership filter (ADDED/OWNED/BOTH)
 *   - `"DENY"`→ NONE, or the entity lacks the column the scope needs → return no rows / 403
 * Pass the entity's `added_by` column and its owner/subject column (user_id / assignee).
 */
export function scopeCondition(
  scope: ScopeId,
  userId: number,
  cols: { addedBy?: AnyMySqlColumn; owner?: AnyMySqlColumn },
): SQL | null | "DENY" {
  switch (scope) {
    case SCOPE.ALL:
      return null;
    case SCOPE.ADDED:
      return cols.addedBy ? eq(cols.addedBy, userId) : "DENY";
    case SCOPE.OWNED:
      return cols.owner ? eq(cols.owner, userId) : "DENY";
    case SCOPE.BOTH: {
      const parts = [
        cols.addedBy ? eq(cols.addedBy, userId) : undefined,
        cols.owner ? eq(cols.owner, userId) : undefined,
      ].filter(Boolean) as SQL[];
      return parts.length ? or(...parts)! : "DENY";
    }
    default:
      return "DENY"; // NONE
  }
}

/** Whether the scope permits acting on a specific record the caller can see (edit/delete checks). */
export function scopeAllowsRecord(
  scope: ScopeId,
  userId: number,
  record: { addedBy?: number | null; owner?: number | null },
): boolean {
  switch (scope) {
    case SCOPE.ALL:
      return true;
    case SCOPE.ADDED:
      return record.addedBy === userId;
    case SCOPE.OWNED:
      return record.owner === userId;
    case SCOPE.BOTH:
      return record.addedBy === userId || record.owner === userId;
    default:
      return false;
  }
}
